import { CSSProperties, FC, useCallback, useContext, type ReactNode } from 'react';
import { theme } from 'antd';
import type { CompositeFieldValue, FieldType } from '#types/System.types';
import type { ArCell as ArCellType, ArCellValue } from '#types';
import { FieldTypeContext } from '../../providers/FieldTypeContext';
import Icon from '../atoms/Icon';
import { useCompositeFieldType } from './useCompositeFieldType';
import { openCompositeFieldSidebar } from './compositeFieldSidebarState';
import { formatFieldValueParts } from './formatPreviewValue';
import { usePdfExportContext } from '../pdfExport/context/PdfExportContext';
import { isShippingTrackerFieldTypeId } from '@/components/rendering/fields/ShippingTrackerUI/isShippingTrackerFieldTypeId';
import { formattedCellValueToString, type FormattedCellValue } from './formattedCellValue';

interface CompositeFieldProps {
  compositeFieldTypeId: string;
  fieldId: string;
  /** Cell value: compound payload or raw CompositeFieldValue. */
  value?: unknown;
  onChange?: (payload: ArCellValue) => void;
  disabled?: boolean;
  isFormulaColumn?: boolean;
  /**
   * Sub-fields a Form Derive projection fills in this cell, each mapped to the
   * sentence explaining the lock — the same one `title` shows on this trigger.
   * Carried into the sidebar, which renders them disabled and hangs the sentence
   * on each: the derive owns those keys while the ones beside them stay the
   * person's, so the whole cell is never locked.
   */
  derivedSubFields?: ReadonlyMap<string, string>;
  tableMode?: boolean;
  style?: CSSProperties;
  /** Native hover line for the trigger, used by Form Derive projected cells. */
  title?: string;
  /**
   * Materializes the cell on the live Valtio proxy and returns it, giving the
   * sidebar table-mode reactivity. Lazy by contract: the owner may not write the
   * proxy during render, so this is called only from the click handler.
   */
  resolveCellValueProxy?: () => ArCellType;
}

/** Renders a composite field trigger button; opens shared Drawer sidebar for editing. */
export const CompositeField: FC<CompositeFieldProps> = ({
  compositeFieldTypeId,
  fieldId,
  value,
  onChange,
  disabled = false,
  isFormulaColumn = false,
  derivedSubFields,
  tableMode = false,
  style,
  title,
  resolveCellValueProxy,
}) => {
  const compositeFieldType = useCompositeFieldType(compositeFieldTypeId);
  const { fieldTypes } = useContext(FieldTypeContext);
  const { token } = theme.useToken();
  const params = extractCompositeParams(value);

  const previewFieldIds = compositeFieldType?.previewFieldIds;

  const handleClick = useCallback(() => {
    if (disabled) return;
    openCompositeFieldSidebar({
      cellValueProxy: resolveCellValueProxy?.(),
      value: params,
      onChange,
      compositeFieldTypeId,
      fieldId,
      isFormulaColumn,
      derivedSubFieldReasons: derivedSubFields,
      tableMode,
    });
  }, [resolveCellValueProxy, disabled, params, onChange, compositeFieldTypeId, fieldId, isFormulaColumn, derivedSubFields, tableMode]);

  const { isPdfExportMode } = usePdfExportContext();
  const previewParts = getPreviewParts(previewFieldIds, params, fieldTypes);
  const hasPreview = previewParts.length > 0;
  const hasShippingPreviewInPdf = isPdfExportMode && previewParts.some((part) =>
    isShippingTrackerFieldTypeId(fieldTypes[part.fieldId]?.fieldDataTypeId)
  );
  const secondaryColor = isPdfExportMode ? token.colorText : token.colorTextTertiary;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`open-composite-field-${fieldId}`}
      title={title}
      role="button"
      tabIndex={disabled ? -1 : 0}
      style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        gap: token.marginXXS,
        overflow: hasShippingPreviewInPdf ? 'visible' : 'hidden',
        textOverflow: hasShippingPreviewInPdf ? 'clip' : 'ellipsis',
        ...(isPdfExportMode ? {
          alignItems: 'flex-start',
          cursor: 'default',
          opacity: 1,
          padding: '1px 2px',
          color: token.colorText,
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: '1.4',
          whiteSpace: hasShippingPreviewInPdf ? 'normal' : 'nowrap',
          wordBreak: hasShippingPreviewInPdf ? 'break-word' : 'normal',
        } : {
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          paddingInlineStart: token.paddingSM,
          color: hasPreview ? token.colorText : token.colorTextQuaternary,
          whiteSpace: 'nowrap',
        }),
        ...style,
      }}
    >
      {hasPreview
        ? previewParts.map((part, i) => {
            const subFieldType = fieldTypes[part.fieldId];
            const isShippingSubField = isShippingTrackerFieldTypeId(subFieldType?.fieldDataTypeId);
            const shippingPdf = isShippingSubField && isPdfExportMode;
            const shippingFormatted = typeof part.formatted === 'object' ? part.formatted : null;
            const shippingInlineLink = isShippingSubField && !isPdfExportMode && shippingFormatted !== null;
            return (
              <span key={part.fieldId}>
                {i > 0 && <span style={{ color: secondaryColor }}> · </span>}
                {part.prefix && <span style={{ color: secondaryColor }}>{part.prefix} </span>}
                {shippingPdf ? renderShippingPreviewLinkForPdf(
                  part.formatted,
                  token.colorText,
                  fieldId,
                  part.fieldId,
                )
                  : shippingInlineLink
                    ? (
                      <a
                        href={shippingFormatted?.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: token.colorLink }}
                        aria-label={`shipping-link-${fieldId}-subfield-${part.fieldId}`}
                      >
                        {shippingFormatted?.display}
                      </a>
                    )
                    : formattedCellValueToString(part.formatted)}
                {part.suffix && <span style={{ color: secondaryColor }}> {part.suffix}</span>}
              </span>
            );
          })
        : '—'}
      {!isPdfExportMode && (
        <Icon.MoreVertical style={{
          color: token.colorTextQuaternary, flexShrink: 0, marginLeft: 'auto'
        }} />
      )}
    </div>
  );
};

/**
 * Renders a single shipping line in PDF export: a link with `display` as visible text, `url` as `href`, or a plain string when not structured.
 */
function renderShippingPreviewLinkForPdf(
  value: FormattedCellValue,
  colorText: string,
  compositeFieldId: string,
  subFieldId: string,
): ReactNode {
  if (typeof value === 'string') return value;
  return (
    <a
      href={value.url}
      target="_blank"
      rel="noreferrer"
      style={{ color: colorText, textDecoration: 'underline' }}
      aria-label={`shipping-pdf-link-${compositeFieldId}-subfield-${subFieldId}`}
    >
      {value.display}
    </a>
  );
}

function extractCompositeParams(value: unknown): CompositeFieldValue | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return value as CompositeFieldValue;
}

type PreviewPart = {
  fieldId: string;
  prefix?: string;
  formatted: FormattedCellValue;
  suffix?: string;
};

function getPreviewParts(
  previewFieldIds: string[] | undefined,
  params: CompositeFieldValue | undefined,
  fieldTypes: Record<string, FieldType>,
): PreviewPart[] {
  if (!previewFieldIds?.length) return [];

  const fields = params?.fields;
  if (!fields) return [];

  const parts: PreviewPart[] = [];
  for (const id of previewFieldIds) {
    const val = fields[id]?.value;
    if (val == null || val === '') continue;
    const { prefix, formatted, suffix } = formatFieldValueParts(val, fieldTypes[id]);
    parts.push({ fieldId: id, prefix, formatted, suffix });
  }
  return parts;
}
